import { useEffect } from "react";
import seoPages from "../seo-pages.json";

const siteOrigin = "https://aiyss.org";
const siteName = "青少年科学者社会接続推進協会（AIYSS）";
const organizationName = "青少年科学者社会接続推進協会";
const ogImageUrl = `${siteOrigin}/assets/images/og-image.png`;
const logoUrl = `${siteOrigin}/assets/images/logo_square.png`;

function normalizePathname(pathname: string) {
  const normalized = pathname.replace(/\/+$/, "");
  return normalized === "" ? "/" : `${normalized}/`;
}

function upsertMeta(
  attribute: "name" | "property",
  key: string,
  content: string,
) {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  );

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.content = content;
}

function upsertCanonical(url: string) {
  let element = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );

  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }

  element.href = url;
}

export function SeoHead() {
  const pathname = normalizePathname(window.location.pathname);
  const page =
    seoPages.find((definition) => definition.path === pathname) ?? seoPages[0];

  useEffect(() => {
    // Quest詳細・応募はPHPがDBから固有メタ情報を返すため、トップページで上書きしない。
    if (/^\/job-quest\/quests\//.test(pathname)) return;
    const canonicalUrl = `${siteOrigin}${page.path}`;

    document.title = page.title;
    upsertMeta("name", "description", page.description);
    upsertMeta("name", "robots", page.robots);
    upsertMeta("property", "og:locale", "ja_JP");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:title", page.title);
    upsertMeta("property", "og:description", page.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", ogImageUrl);
    upsertMeta("property", "og:image:width", "1000");
    upsertMeta("property", "og:image:height", "750");
    upsertMeta("property", "og:image:alt", "AIYSS");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", page.title);
    upsertMeta("name", "twitter:description", page.description);
    upsertMeta("name", "twitter:image", ogImageUrl);
    upsertCanonical(canonicalUrl);

    let structuredData = document.head.querySelector<HTMLScriptElement>(
      "#aiyss-structured-data",
    );
    if (!structuredData) {
      structuredData = document.createElement("script");
      structuredData.id = "aiyss-structured-data";
      structuredData.type = "application/ld+json";
      document.head.appendChild(structuredData);
    }

    structuredData.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${siteOrigin}/#organization`,
          name: organizationName,
          alternateName: "AIYSS",
          url: `${siteOrigin}/`,
          logo: {
            "@type": "ImageObject",
            url: logoUrl,
          },
        },
        {
          "@type": "WebSite",
          "@id": `${siteOrigin}/#website`,
          url: `${siteOrigin}/`,
          name: siteName,
          inLanguage: "ja",
          publisher: { "@id": `${siteOrigin}/#organization` },
        },
        {
          "@type": "WebPage",
          "@id": `${canonicalUrl}#webpage`,
          url: canonicalUrl,
          name: page.title,
          description: page.description,
          inLanguage: "ja",
          isPartOf: { "@id": `${siteOrigin}/#website` },
          about: { "@id": `${siteOrigin}/#organization` },
        },
      ],
    });
  }, [page, pathname]);

  return null;
}
